import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  ArrowRight,
  BarChart3,
  Building2,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  DatabaseBackup,
  FileText,
  Gauge,
  Image,
  LockKeyhole,
  Newspaper,
  Search,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";
import AdminLayout from "../../components/admin/AdminLayout";
import { api } from "../../lib/api";
import { getAdminRole } from "../../lib/auth";

const defaultStats = {
  news: 0,
  events: 0,
  notices: 0,
  departments: 0,
  downloads: 0,
  staff: 0,
  campusLocations: 0,
  galleryAlbums: 0,
  galleryMedia: 0,
  journalIssues: 0,
  journalArticles: 0,
  tickets: 0,
  alumni: 0,
  partnerships: 0,
  projects: 0,
  seoPages: 0,
  pageViews: 0,
  securityLogs: 0,
  admins: 0,
  backups: 0,
  navigation: 0,
  approvals: 0,
  searchLogs: 0,
  institutionalPages: 0,
};

const endpoints = [
  ["news", "/api/admin/news"],
  ["events", "/api/admin/events"],
  ["notices", "/api/admin/notices"],
  ["departments", "/api/admin/departments"],
  ["institutionalPages", "/api/admin/institutional-pages"],
  ["downloads", "/api/admin/downloads"],
  ["staff", "/api/admin/staff"],
  ["campusLocations", "/api/admin/campus-locations"],
  ["galleryAlbums", "/api/admin/gallery-albums"],
  ["galleryMedia", "/api/admin/gallery-media"],
  ["journalIssues", "/api/admin/journal-issues"],
  ["journalArticles", "/api/admin/journal-articles"],
  ["alumni", "/api/admin/alumni"],
  ["partnerships", "/api/admin/partnerships"],
  ["projects", "/api/admin/projects"],
  ["tickets", "/api/admin/support-tickets"],
  ["seoPages", "/api/admin/seo-pages"],
  ["analytics", "/api/admin/analytics/overview"],
  ["security", "/api/admin/security/overview"],
  ["navigation", "/api/admin/navigation-items"],
  ["approvals", "/api/admin/content-approvals?status=all"],
  ["searchLogs", "/api/admin/search-logs"],
];

const newsEventEndpoints = [
  ["news", "/api/admin/news"],
  ["events", "/api/admin/events"],
];

const statCards = [
  { label: "News", key: "news", to: "/adxm/news", icon: Newspaper, tone: "blue" },
  { label: "Events", key: "events", to: "/adxm/events", icon: CalendarDays, tone: "violet" },
  { label: "Notices", key: "notices", to: "/adxm/notices", icon: FileText, tone: "rose" },
  { label: "Institutional Pages", key: "institutionalPages", to: "/adxm/institutional-pages", icon: Building2, tone: "emerald" },
  { label: "Staff", key: "staff", to: "/adxm/staff", icon: Users, tone: "amber" },
  { label: "Downloads", key: "downloads", to: "/adxm/downloads", icon: FileText, tone: "cyan" },
  { label: "Gallery Media", key: "galleryMedia", to: "/adxm/gallery-media", icon: Image, tone: "fuchsia" },
  { label: "Page Views", key: "pageViews", to: "/adxm/analytics", icon: BarChart3, tone: "indigo" },
];

const newsEventStatCards = statCards.filter((card) => ["news", "events"].includes(card.key));

const moduleGroups = [
  {
    title: "Publish & communicate",
    icon: Newspaper,
    links: [
      ["Create news", "/adxm/news/create"],
      ["Add event", "/adxm/events/create"],
      ["Manage notices", "/adxm/notices"],
      ["Content approvals", "/adxm/content-approvals"],
    ],
  },
  {
    title: "Institutional pages",
    icon: Building2,
    links: [
      ["All institutional pages", "/adxm/institutional-pages"],
      ["Departments", "/adxm/departments"],
      ["Centres", "/adxm/centers"],
      ["Directorates", "/adxm/directorates"],
      ["Units", "/adxm/units"],
    ],
  },
  {
    title: "Resources & media",
    icon: Image,
    links: [
      ["Downloads", "/adxm/downloads"],
      ["Staff directory", "/adxm/staff"],
      ["Gallery albums", "/adxm/gallery-albums"],
      ["Campus map", "/adxm/campus-locations"],
    ],
  },
  {
    title: "Security & growth",
    icon: ShieldCheck,
    links: [
      ["Security center", "/adxm/security"],
      ["Backup center", "/adxm/backups"],
      ["SEO manager", "/adxm/seo"],
      ["Search insights", "/adxm/search-insights"],
    ],
  },
];

function getCount(payload) {
  if (Array.isArray(payload)) return payload.length;
  if (payload && Array.isArray(payload.rows)) return payload.rows.length;
  if (payload && Array.isArray(payload.data)) return payload.data.length;
  return 0;
}

function StatCard({ card, value }) {
  const Icon = card.icon;
  return (
    <Link to={card.to} className={`fpb-admin-stat-card tone-${card.tone}`}>
      <span className="fpb-admin-stat-icon"><Icon size={22} /></span>
      <span className="fpb-admin-stat-copy">
        <span>{card.label}</span>
        <strong>{value}</strong>
      </span>
      <ArrowRight className="fpb-admin-stat-arrow" size={18} />
    </Link>
  );
}

export default function AdminDashboard() {
  const role = getAdminRole();
  const isNewsEventRole = role === "news_event_admin";
  const activeEndpoints = isNewsEventRole ? newsEventEndpoints : endpoints;
  const visibleStatCards = isNewsEventRole ? newsEventStatCards : statCards;
  const [stats, setStats] = useState(defaultStats);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState("");

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      setError("");
      const results = await Promise.allSettled(activeEndpoints.map(([key, url]) => api(url).then((data) => [key, data])));
      if (!mounted) return;

      const nextStats = { ...defaultStats };
      const failed = [];

      results.forEach((result) => {
        if (result.status !== "fulfilled") {
          failed.push(result.reason?.message || "One dashboard endpoint failed");
          return;
        }
        const [key, data] = result.value;
        if (key === "analytics") {
          nextStats.pageViews = data?.total_views || 0;
          return;
        }
        if (key === "security") {
          nextStats.securityLogs = data?.total_logs || 0;
          nextStats.admins = data?.total_admins || 0;
          nextStats.backups = (data?.recent_backups || []).length;
          return;
        }
        nextStats[key] = getCount(data);
      });

      setStats(nextStats);
      setLastUpdated(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
      setError(failed.length ? `${failed.length} dashboard module(s) could not load. Open the related module for details.` : "");
      setLoading(false);
    }

    load();
    return () => {
      mounted = false;
    };
  }, [activeEndpoints]);

  const contentTotal = useMemo(
    () => isNewsEventRole ? stats.news + stats.events : stats.news + stats.events + stats.notices + stats.institutionalPages + stats.downloads + stats.staff + stats.galleryMedia,
    [stats, isNewsEventRole]
  );

  const healthItems = [
    { label: "CMS modules", value: "Ready", icon: CheckCircle2, good: true },
    { label: "Audit logs", value: stats.securityLogs, icon: Activity, good: true },
    { label: "Backups", value: stats.backups, icon: DatabaseBackup, good: stats.backups > 0 },
    { label: "Admin users", value: stats.admins, icon: LockKeyhole, good: stats.admins > 0 },
  ];

  const visibleHealthItems = isNewsEventRole
    ? [
        { label: "News module", value: "Ready", icon: Newspaper, good: true },
        { label: "Events module", value: "Ready", icon: CalendarDays, good: true },
      ]
    : healthItems;

  const qualityItems = isNewsEventRole
    ? [
        ["News records", stats.news, 50],
        ["Event records", stats.events, 50],
      ]
    : [
        ["Institutional content", stats.institutionalPages, 80],
        ["Media coverage", stats.galleryMedia, 70],
        ["Downloads/resources", stats.downloads, 65],
        ["SEO pages", stats.seoPages, 55],
      ];

  const primaryKpis = [
    {
      label: "Managed content",
      value: contentTotal,
      helper: "Published and manageable records",
      icon: Building2,
      tone: "emerald",
    },
    {
      label: "Page views",
      value: stats.pageViews,
      helper: "Tracked public website visits",
      icon: BarChart3,
      tone: "blue",
    },
    {
      label: "Pending workflow",
      value: stats.approvals,
      helper: "Items waiting for review",
      icon: ClipboardCheck,
      tone: "amber",
    },
    {
      label: "System modules",
      value: loading ? "..." : "Ready",
      helper: "CMS control panels available",
      icon: Gauge,
      tone: "violet",
    },
  ];

  const visiblePrimaryKpis = isNewsEventRole
    ? [
        { label: "News posts", value: stats.news, helper: "News records you can manage", icon: Newspaper, tone: "blue" },
        { label: "Events", value: stats.events, helper: "Event records you can manage", icon: CalendarDays, tone: "violet" },
        { label: "Content access", value: "News + Events", helper: "Limited admin role", icon: ShieldCheck, tone: "emerald" },
        { label: "Modules", value: loading ? "..." : "Ready", helper: "Publishing tools available", icon: Gauge, tone: "amber" },
      ]
    : primaryKpis;

  const priorityActions = [
    { label: "Create News", to: "/adxm/news/create", icon: Newspaper, helper: "Publish official update" },
    { label: "Add Department", to: "/adxm/institutional-pages/create", icon: Building2, helper: "Create a new institutional page" },
    { label: "Upload Media", to: "/adxm/gallery-media/create", icon: Image, helper: "Add gallery image or video" },
    { label: "Run UI Audit", to: "/adxm/ui-ux", icon: Sparkles, helper: "Check responsiveness and quality" },
  ];

  const visiblePriorityActions = isNewsEventRole
    ? [
        { label: "Create News", to: "/adxm/news/create", icon: Newspaper, helper: "Publish official update" },
        { label: "Add Event", to: "/adxm/events/create", icon: CalendarDays, helper: "Publish official event" },
      ]
    : priorityActions;

  const visibleModuleGroups = isNewsEventRole
    ? [
        {
          title: "News & Events Publishing",
          icon: Newspaper,
          links: [
            ["Create news", "/adxm/news/create"],
            ["Manage news", "/adxm/news"],
            ["Add event", "/adxm/events/create"],
            ["Manage events", "/adxm/events"],
          ],
        },
      ]
    : moduleGroups;

  return (
    <AdminLayout title="Dashboard" subtitle={isNewsEventRole ? "Post and manage official News and Events only." : "Manage the official Federal Polytechnic Bida website from a focused CMS control panel."}>
      <div className="fpb-admin-dashboard fpb-admin-dashboard-v22">
        <section className="fpb-admin-command-strip">
          <div>
            <span className="fpb-admin-eyebrow"><Sparkles size={14} /> Phase 22 Admin Shell</span>
            <h1>Website operations dashboard</h1>
            <p>
              Publish content, maintain departments/centres/directorates/units, monitor website health, and control the CMS without the public website layout getting in the way.
            </p>
            <div className="fpb-admin-sync-note">Last sync: {loading ? "Loading..." : lastUpdated || "Ready"}</div>
          </div>
          <div className="fpb-admin-command-actions">
            <Link to="/adxm/institutional-pages/create" className="fpb-admin-btn-primary"><Building2 size={17} /> Add Page</Link>
            <Link to="/adxm/news/create" className="fpb-admin-btn-secondary"><Newspaper size={17} /> Publish News</Link>
            <Link to="/" target="_blank" rel="noreferrer" className="fpb-admin-btn-secondary"><ArrowRight size={17} /> View Site</Link>
          </div>
        </section>

        {error && <div className="fpb-admin-soft-alert">{error}</div>}

        <section className="fpb-admin-kpi-grid-v22">
          {visiblePrimaryKpis.map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.label} className={`fpb-admin-kpi-card-v22 tone-${item.tone}`}>
                <span className="fpb-admin-kpi-icon-v22"><Icon size={22} /></span>
                <div>
                  <p>{item.label}</p>
                  <strong>{loading && item.label !== "System modules" ? "..." : item.value}</strong>
                  <small>{item.helper}</small>
                </div>
              </div>
            );
          })}
        </section>

        <section className="fpb-admin-work-grid-v22">
          <div className="fpb-admin-panel-card fpb-admin-panel-premium">
            <div className="fpb-admin-panel-heading">
              <div>
                <span className="fpb-admin-mini-label">Priority actions</span>
                <h2>Start common tasks</h2>
              </div>
              <Link to="/adxm/institutional-pages" className="fpb-admin-mini-link">Manage content</Link>
            </div>
            <div className="fpb-admin-action-card-grid">
              {visiblePriorityActions.map((action) => {
                const Icon = action.icon;
                return (
                  <Link key={action.to} to={action.to} className="fpb-admin-action-card-v22">
                    <span><Icon size={22} /></span>
                    <div>
                      <strong>{action.label}</strong>
                      <p>{action.helper}</p>
                    </div>
                    <ArrowRight size={17} />
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="fpb-admin-panel-card fpb-admin-panel-premium">
            <div className="fpb-admin-panel-heading">
              <div>
                <span className="fpb-admin-mini-label">System health</span>
                <h2>CMS status</h2>
              </div>
              <Gauge className="text-emerald-700" size={24} />
            </div>
            <div className="space-y-3">
              {visibleHealthItems.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.label} className="fpb-admin-health-row-v22">
                    <span className={item.good ? "good" : "warn"}><Icon size={18} /></span>
                    <div>
                      <p>{item.label}</p>
                      <strong>{loading && typeof item.value === "number" ? "..." : item.value}</strong>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="fpb-admin-stat-grid fpb-admin-stat-grid-v22">
          {visibleStatCards.map((card) => (
            <StatCard key={card.key} card={card} value={loading ? "..." : stats[card.key]} />
          ))}
        </section>

        <section className="fpb-admin-two-column-v22">
          <div className="fpb-admin-panel-card fpb-admin-panel-premium">
            <div className="fpb-admin-panel-heading">
              <div>
                <span className="fpb-admin-mini-label">Quick modules</span>
                <h2>Most used sections</h2>
              </div>
              <Activity className="text-slate-500" size={22} />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {visibleModuleGroups.map((group) => {
                const Icon = group.icon;
                return (
                  <div key={group.title} className="fpb-admin-module-card-v22">
                    <div className="mb-4 flex items-center gap-3">
                      <span className="fpb-admin-module-icon"><Icon size={20} /></span>
                      <h3>{group.title}</h3>
                    </div>
                    <div className="space-y-2">
                      {group.links.map(([label, to]) => (
                        <Link key={to} to={to} className="fpb-admin-module-link">
                          <span>{label}</span>
                          <ArrowRight size={15} />
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-6">
            <div className="fpb-admin-panel-card fpb-admin-panel-premium">
              <div className="fpb-admin-panel-heading">
                <div>
                  <span className="fpb-admin-mini-label">Content quality</span>
                  <h2>Coverage overview</h2>
                </div>
                <ClipboardCheck className="text-blue-700" size={24} />
              </div>
              <div className="space-y-4">
                {qualityItems.map(([label, value, max]) => {
                  const percentage = Math.min(100, Math.round(((Number(value) || 0) / max) * 100));
                  return (
                    <div key={label}>
                      <div className="mb-1 flex justify-between gap-4 text-xs font-black text-slate-600">
                        <span>{label}</span>
                        <span>{loading ? "..." : value}</span>
                      </div>
                      <div className="fpb-admin-progress-track"><span style={{ width: `${percentage}%` }} /></div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="fpb-admin-panel-card fpb-admin-panel-premium">
              <div className="fpb-admin-panel-heading">
                <div>
                  <span className="fpb-admin-mini-label">Readiness</span>
                  <h2>Control checklist</h2>
                </div>
                <CheckCircle2 className="text-emerald-700" size={24} />
              </div>
              <div className="fpb-admin-compact-check-list">
                {(isNewsEventRole ? [
                  ["News create/edit/publish", Newspaper],
                  ["Events create/edit/publish", CalendarDays],
                  ["Other modules hidden", ShieldCheck],
                ] : [
                  ["Protected admin routes", ShieldCheck],
                  ["Institutional pages CMS", Building2],
                  ["Security and backup center", DatabaseBackup],
                  ["SEO and analytics manager", BarChart3],
                  ["Search insights and mega menu", Search],
                ]).map(([label, Icon]) => (
                  <div key={label} className="fpb-admin-check-item-v22">
                    <Icon size={17} />
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    </AdminLayout>
  );
}
